/**
 * Filtros del historial de pedidos del backoffice (ADM-09): de la URL a un
 * objeto y de vuelta, y la lista de los que estan puestos. Mismo modelo que
 * `filtros-productos.ts`: la URL es la unica fuente.
 *
 * `pedido` tambien vive en la URL pero no es un filtro: es el pedido cuyo
 * modal esta abierto, y abrirlo no puede volver a pedir la tabla.
 */
import type {
  EstadoPedido,
  FiltroDocumentoPedidos,
  FiltrosPedidosDto,
  OrdenPedidosAdmin,
  TipoDocumento,
  TipoEntrega,
} from '@bandu/compartido';

import { textoEntrega } from './entrega';
import { pesos } from './formato';
import { cantidad, compactar, texto, unoDe } from './parametros-url';
import type { FiltroActivo as FiltroActivoDe } from './parametros-url';
import { ESTADO_PAGO } from './textos-pedido';

export type FiltrosPedidos = Omit<FiltrosPedidosDto, 'pagina' | 'porPagina'>;
export type FiltroActivo = FiltroActivoDe<FiltrosPedidos>;

/** Parametro de la URL con el id del pedido abierto en el modal. */
export const PARAMETRO_PEDIDO = 'pedido';

export const ESTADOS_PEDIDO: EstadoPedido[] = ['pagado', 'pendiente_pago', 'rechazado', 'anulado'];

export const OPCIONES_DOCUMENTO: { valor: FiltroDocumentoPedidos; texto: string }[] = [
  { valor: 'por_emitir', texto: 'Por emitir' },
  { valor: 'emitido', texto: 'Emitido' },
];

export const ENTREGAS: TipoEntrega[] = ['despacho', 'retiro', 'coordinar'];
const DOCUMENTOS: TipoDocumento[] = ['boleta', 'factura'];

export const ORDENES_PEDIDOS: { valor: OrdenPedidosAdmin; texto: string }[] = [
  { valor: 'recientes', texto: 'Mas recientes' },
  { valor: 'antiguos', texto: 'Mas antiguos' },
  { valor: 'total_desc', texto: 'Mayor total' },
  { valor: 'total_asc', texto: 'Menor total' },
];

function dia(valor: string | null): string | undefined {
  return valor !== null && /^\d{4}-\d{2}-\d{2}$/.test(valor) ? valor : undefined;
}

export function leerFiltros(parametros: URLSearchParams): FiltrosPedidos {
  const orden = unoDe(
    parametros.get('orden'),
    ORDENES_PEDIDOS.map((opcion) => opcion.valor),
  );

  return compactar({
    termino: texto(parametros.get('termino')),
    estado: unoDe(parametros.get('estado'), ESTADOS_PEDIDO),
    documento: unoDe(
      parametros.get('documento'),
      OPCIONES_DOCUMENTO.map((opcion) => opcion.valor),
    ),
    tipoEntrega: unoDe(parametros.get('tipoEntrega'), ENTREGAS),
    tipoDocumento: unoDe(parametros.get('tipoDocumento'), DOCUMENTOS),
    desde: dia(parametros.get('desde')),
    hasta: dia(parametros.get('hasta')),
    totalMin: cantidad(parametros.get('totalMin')),
    totalMax: cantidad(parametros.get('totalMax')),
    // El orden por defecto no se escribe: la URL sin filtros queda limpia.
    orden: orden !== 'recientes' ? orden : undefined,
  });
}

export function escribirFiltros(filtros: FiltrosPedidos): URLSearchParams {
  const parametros = new URLSearchParams();
  for (const [clave, valor] of Object.entries(compactar(filtros))) {
    if (clave === 'orden' && valor === 'recientes') continue;
    parametros.set(clave, String(valor));
  }
  return parametros;
}

export function conCambio(
  filtros: FiltrosPedidos,
  cambio: Partial<FiltrosPedidos>,
): FiltrosPedidos {
  return compactar({ ...filtros, ...cambio });
}

/** Limpiar todo quita los filtros pero respeta el orden: ordenar no es filtrar. */
export function sinFiltros(filtros: FiltrosPedidos): FiltrosPedidos {
  return compactar({ orden: filtros.orden });
}

/** `2026-09-22` -> `22-09-2026`, como se escriben las fechas en Chile. */
function diaChileno(valor: string): string {
  return valor.split('-').reverse().join('-');
}

function textoRango(
  minimo: string | undefined,
  maximo: string | undefined,
  nombre: string,
): string {
  if (minimo !== undefined && maximo !== undefined) return `${nombre} entre ${minimo} y ${maximo}`;
  if (minimo !== undefined) return `${nombre} desde ${minimo}`;
  return `${nombre} hasta ${maximo ?? ''}`;
}

/** Un chip por filtro puesto, en el orden de la barra. */
export function filtrosActivos(filtros: FiltrosPedidos): FiltroActivo[] {
  const activos: FiltroActivo[] = [];
  const agregar = (clave: string, texto: string, cambio: Partial<FiltrosPedidos>) => {
    activos.push({ clave, texto, sinEste: conCambio(filtros, cambio) });
  };

  if (filtros.termino !== undefined) {
    agregar('termino', `Busqueda: "${filtros.termino}"`, { termino: undefined });
  }
  if (filtros.estado !== undefined) {
    agregar('estado', ESTADO_PAGO[filtros.estado].texto, { estado: undefined });
  }
  if (filtros.documento !== undefined) {
    agregar(
      'documento',
      filtros.documento === 'por_emitir' ? 'Documento por emitir' : 'Documento emitido',
      { documento: undefined },
    );
  }
  if (filtros.tipoEntrega !== undefined) {
    agregar('tipoEntrega', textoEntrega(filtros.tipoEntrega), { tipoEntrega: undefined });
  }
  if (filtros.tipoDocumento !== undefined) {
    agregar('tipoDocumento', filtros.tipoDocumento === 'factura' ? 'Factura' : 'Boleta', {
      tipoDocumento: undefined,
    });
  }
  if (filtros.desde !== undefined || filtros.hasta !== undefined) {
    const desde = filtros.desde !== undefined ? diaChileno(filtros.desde) : undefined;
    const hasta = filtros.hasta !== undefined ? diaChileno(filtros.hasta) : undefined;
    agregar(
      'fechas',
      desde !== undefined && desde === hasta ? `Fecha: ${desde}` : textoRango(desde, hasta, 'Fecha'),
      { desde: undefined, hasta: undefined },
    );
  }
  if (filtros.totalMin !== undefined || filtros.totalMax !== undefined) {
    agregar(
      'total',
      textoRango(
        filtros.totalMin !== undefined ? pesos(filtros.totalMin) : undefined,
        filtros.totalMax !== undefined ? pesos(filtros.totalMax) : undefined,
        'Total',
      ),
      { totalMin: undefined, totalMax: undefined },
    );
  }

  return activos;
}
